import { create } from 'zustand';
import { ApiError } from '@/lib/api/apiClient';

interface MemberOnlyDialogState {
  open: boolean;
  message: string | null;
  openDialog: (message?: string) => void;
  close: () => void;
}

/**
 * Global trigger for the "Members Only" dialog — a venue account hit a member-only endpoint
 * (favorites, reviews, bookings, etc. all return the same 403 shape from the API). Kept as its
 * own tiny store, not local component state, since the trigger point is often non-component
 * code (e.g. memberStore's toggleFavorite) with no dialog of its own to render.
 */
export const useMemberOnlyDialogStore = create<MemberOnlyDialogState>((set) => ({
  open: false,
  message: null,
  openDialog: (message) => set({ open: true, message: message ?? null }),
  close: () => set({ open: false }),
}));

/** Strip inline HTML (`<br/>` etc. — written for an older UI) down to plain text. */
function plainText(message: string): string {
  return message.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * True for the specific 403 several member-only endpoints (favorites, reviews, bookings, gift
 * cards, tickets, bottles, careers, contact, talent…) return when a non-member account calls
 * them — same literal message reused server-side (see e.g. MemberController@toggleFavoriteStatus).
 * No stable error `code` exists for this yet, so this matches on the message text itself.
 */
export function isMemberOnlyError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  return plainText(err.message).toLowerCase().includes('sign up as a member');
}

/** Convenience: show the dialog if this is a member-only error, else return false so the
 *  caller can fall back to its normal error handling (toast, etc). */
export function handleMemberOnlyError(err: unknown): boolean {
  if (!isMemberOnlyError(err)) return false;
  useMemberOnlyDialogStore.getState().openDialog(plainText((err as ApiError).message));
  return true;
}
